<template>
  <v-app>
    <v-navigation-drawer
      v-if="loggedIn"
      app
      class="app-drawer no-print"
      :permanent="$vuetify.breakpoint.mdAndUp"
      :temporary="$vuetify.breakpoint.smAndDown"
      :value="$vuetify.breakpoint.mdAndUp || drawer"
      width="296"
      mobile-breakpoint="960"
      @input="drawer = $event"
    >
      <div class="drawer-shell">
        <div class="drawer-brand">
          <div class="drawer-brand__mark">AC</div>
          <div>
            <p>App Companion</p>
            <span>Inventory Workspace</span>
          </div>
        </div>

        <v-list nav dense class="drawer-list">
          <v-list-item
            v-for="item in visiblePrimaryRoutes"
            :key="item.name"
            :to="item.to"
            class="drawer-list__item"
            :class="{ 'drawer-list__item--active': isPrimaryActive(item) }"
            @click="closeDrawerOnMobile"
          >
            <v-list-item-icon>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.label }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <div v-if="activeRoute" class="drawer-section">
          <div class="drawer-section__title">{{ activeRoute.label }}</div>
          <v-list nav dense class="drawer-list drawer-list--secondary">
            <v-list-item
              v-for="route in filteredActiveRoutes"
              :key="route.name"
              :to="route.url"
              class="drawer-list__item drawer-list__item--secondary"
              @click="closeDrawerOnMobile"
            >
              <v-list-item-icon>
                <v-icon>{{ route.icon || activeRoute.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ route.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <div class="drawer-spacer"></div>

        <div class="drawer-footer">
          <v-btn text block class="drawer-footer__button" to="/password-reset" @click="closeDrawerOnMobile">
            <v-icon left>mdi-lock-reset</v-icon>
            Password
          </v-btn>
          <v-btn depressed block color="primary" class="drawer-footer__button drawer-footer__button--primary" to="/login">
            <v-icon left>mdi-logout</v-icon>
            Sign out
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <v-app-bar
      v-if="loggedIn"
      app
      flat
      class="app-topbar no-print"
      :height="$vuetify.breakpoint.xsOnly ? 64 : 72"
    >
      <v-btn
        v-if="$vuetify.breakpoint.smAndDown && showMobileBack"
        icon
        class="topbar-menu"
        aria-label="Back"
        @click="onMobileBack"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-app-bar-nav-icon
        v-else-if="$vuetify.breakpoint.smAndDown"
        class="topbar-menu"
        @click.stop="drawer = true"
      ></v-app-bar-nav-icon>

      <div class="topbar-title">
        <span>{{ activeSectionLabel }}</span>
        <h1>{{ pageTitle }}</h1>
      </div>

      <v-spacer></v-spacer>

      <v-tooltip v-if="isAdmin || isOwner" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="topbar-action" to="/settings" v-bind="attrs" v-on="on" aria-label="Settings">
            <v-icon>mdi-cog</v-icon>
          </v-btn>
        </template>
        <span>Settings</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="topbar-action" to="/login" v-bind="attrs" v-on="on" aria-label="Sign out">
            <v-icon>mdi-logout</v-icon>
          </v-btn>
        </template>
        <span>Sign out</span>
      </v-tooltip>
    </v-app-bar>

    <v-main :class="['app-main', { 'app-main--auth': !loggedIn }]">
      <vue-confirm-dialog></vue-confirm-dialog>
      <div :class="loggedIn ? 'app-content' : 'auth-content'">
        <router-view />
      </div>
    </v-main>
  </v-app>
</template>

<script>
import { userService } from '@/services';

export default {
  name: 'App',
  data() {
    return {
      title: 'Home',
      isOwner: false,
      isAdmin: false,
      isStaff: false,
      drawer: false,
      primaryRoutes: [
        { name: 'apps', label: 'Apps', to: '/apps', icon: 'mdi-view-dashboard', adminOnly: true },
        { name: 'suppliers', label: 'Suppliers', to: '/suppliers', icon: 'mdi-truck' },
        { name: 'categories', label: 'Categories', to: '/categories', icon: 'mdi-format-list-checks' },
        { name: 'products', label: 'Products', to: '/products', icon: 'mdi-package-variant' },
        { name: 'inventory', label: 'Inventory', to: '/inventory', icon: 'mdi-barcode-scan' },
        { name: 'reports', label: 'Reports', to: '/reports', icon: 'mdi-file-chart' },
        { name: 'promotions', label: 'Promotions', to: '/promotions', icon: 'mdi-sale' },
        { name: 'users', label: 'Users', to: '/users', icon: 'mdi-account-multiple', managerOnly: true },
        { name: 'settings', label: 'Settings', to: '/settings', icon: 'mdi-cog', managerOnly: true },
      ],
      subRoutes: [
        {
          name: 'reports',
          label: 'Reports',
          url: '/reports',
          icon: 'mdi-file-chart',
          routes: [
            { name: 'Takings', url: '/reports/takings', icon: 'mdi-cash-register' },
            { name: 'Orders', url: '/reports/orders', icon: 'mdi-receipt-text-outline' },
            { name: 'Online Orders', url: '/reports/onlineorders', icon: 'mdi-web' },
            { name: 'Staff Product Sales', url: '/reports/staffproductsales', icon: 'mdi-account' },
            { name: 'Staff Orders', url: '/reports/stafforders', icon: 'mdi-account-details' },
            { name: 'Refunds', url: '/reports/refunds', icon: 'mdi-cash-refund' },
            { name: 'Deleted Audit', url: '/reports/audit', icon: 'mdi-clipboard-search-outline' },
            { name: 'Current Stock', url: '/reports/currentstock', icon: 'mdi-warehouse' },
            { name: 'Ordering', url: '/reports/ordering', icon: 'mdi-cart-arrow-down' },
            { name: 'Low Stock', url: '/reports/lowstock', icon: 'mdi-alert-circle-outline' },
            { name: 'Write Off', url: '/reports/writeoff', icon: 'mdi-archive-remove-outline' },
            { name: 'Best/Worst Sellers', url: '/reports/bestsellers', icon: 'mdi-poll' },
            { name: 'Timesheets', url: '/reports/timesheets', icon: 'mdi-clock-outline' },
            { name: 'Category Performance', url: '/reports/categoryperformance', icon: 'mdi-chart-donut' },
            { name: 'Stock Usage', url: '/reports/stockusage', icon: 'mdi-bottle-tonic-outline' },
          ]
        },
        {
          name: 'products',
          label: 'Products',
          url: '/products',
          icon: 'mdi-package-variant',
          routes: [
            { name: 'Products', url: '/products/list', icon: 'mdi-package-variant' },
            { name: 'Extras', url: '/products/extras', icon: 'mdi-plus-box-multiple-outline' },
          ]
        },
      ],
      activeRoute: null
    }
  },
  computed: {
    loggedIn() {
      return this.$store.state.status.loggedIn;
    },
    visiblePrimaryRoutes() {
      return this.primaryRoutes
        .filter(route => !route.adminOnly || this.isAdmin)
        .filter(route => !route.managerOnly || this.isAdmin || this.isOwner)
        .map(route => {
          if (route.name === 'reports' && this.isStaff) {
            return { ...route, to: '/reports/lowstock' };
          }
          return route;
        });
    },
    filteredActiveRoutes() {
      if (!this.activeRoute) return [];
      return this.getVisibleRoutes(this.activeRoute);
    },
    pageTitle() {
      return this.formatTitle(this.title);
    },
    activeSectionLabel() {
      if (this.activeRoute) {
        return this.activeRoute.label;
      }

      const activeRoute = this.visiblePrimaryRoutes.find(route => this.isPrimaryActive(route));
      return activeRoute ? activeRoute.label : 'Workspace';
    },
    showMobileBack() {
      const path = this.$route.path;
      if (path === '/' || path === '/login') return false;
      return true;
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        this.isAdmin = userService.isAdmin();
        this.isStaff = userService.isStaff();
        this.isOwner = userService.isOwner();

        const parent = this.subRoutes.find(r =>
          r.url === to.path ||
          (to.path === '/' && r.name === 'reports') ||
          r.routes.some(route => route.url === to.path)
        );

        if (parent) {
          this.activeRoute = parent;

          const visible = this.getVisibleRoutes(parent);
          if (to.path === parent.url && visible.length) {
            this.$router.replace({ path: visible[0].url });
          }
        } else {
          this.activeRoute = null;
        }

        this.title = to.path === '/' ? 'Takings' : to.name;
        this.closeDrawerOnMobile();
      }
    }
  },
  methods: {
    onMobileBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push('/');
      }
    },
    getVisibleRoutes(parent) {
      if (this.isOwner || this.isAdmin) {
        return parent.routes;
      }

      const allowedReportUrls = [
        '/reports/currentstock',
        '/reports/ordering',
        '/reports/lowstock'
      ];

      if (parent.name === 'reports') {
        return parent.routes.filter(route => allowedReportUrls.includes(route.url));
      }
      return parent.routes;
    },
    isPrimaryActive(item) {
      if (item.name === 'reports') {
        return this.$route.path === '/' || this.$route.path.startsWith('/reports');
      }

      if (item.name === 'products') {
        return this.$route.path.startsWith('/products');
      }

      return this.$route.path === item.to || this.$route.path.startsWith(`${item.to}/`);
    },
    formatTitle(title) {
      if (!title) {
        return 'Dashboard';
      }

      return title.replace(/([a-z])([A-Z])/g, '$1 $2');
    },
    closeDrawerOnMobile() {
      if (this.$vuetify && this.$vuetify.breakpoint.smAndDown) {
        this.drawer = false;
      }
    }
  }
};
</script>

<style lang="scss">
@import "./breakpoints.scss";

.app-main {
  background:
    linear-gradient(135deg, rgba(15, 118, 110, 0.08) 0%, rgba(250, 204, 21, 0.07) 36%, rgba(248, 250, 252, 0) 72%),
    #f6f8fb;
  min-height: 100vh;
}

.app-main--auth {
  background:
    linear-gradient(135deg, rgba(15, 118, 110, 0.14) 0%, rgba(217, 119, 6, 0.1) 48%, rgba(255, 255, 255, 0) 100%),
    #f7faf8;
}

.app-content {
  width: 100%;
  max-width: 1680px;
  margin: 0 auto;
  padding: 16px;

  @include md {
    padding: 24px;
  }

  @include xl {
    padding: 32px;
  }
}

.auth-content {
  min-height: 100vh;
}

.app-topbar.v-app-bar {
  background: rgba(255, 255, 255, 0.92) !important;
  border-bottom: 1px solid rgba(31, 41, 55, 0.08) !important;
  backdrop-filter: blur(16px);
  color: #172026 !important;
}

.topbar-menu {
  margin-right: 4px;
}

.topbar-title {
  min-width: 0;

  span {
    color: #64748b;
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1.2;
    text-transform: uppercase;
  }

  h1 {
    color: #111827;
    font-size: 20px;
    font-weight: 800;
    line-height: 1.2;
    margin: 2px 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @include md {
      font-size: 24px;
    }
  }
}

.topbar-action.v-btn {
  color: #334155 !important;
  margin-left: 4px;
}

.app-drawer.v-navigation-drawer {
  background: #ffffff !important;
  border-right: 1px solid rgba(31, 41, 55, 0.08) !important;
  box-shadow: 12px 0 30px rgba(15, 23, 42, 0.04) !important;
}

.drawer-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 18px 14px;
}

.drawer-brand {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
  min-width: 0;
  padding: 0 6px;

  p {
    color: #111827;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.15;
    margin: 0;
  }

  span {
    color: #64748b;
    display: block;
    font-size: 12px;
    line-height: 1.2;
    margin-top: 3px;
  }
}

.drawer-brand__mark {
  align-items: center;
  background: #0f766e;
  border-radius: 8px;
  color: #ffffff;
  display: flex;
  flex: 0 0 40px;
  font-weight: 800;
  height: 40px;
  justify-content: center;
}

.drawer-list.v-list {
  padding: 0;
}

.drawer-list__item.v-list-item {
  border-radius: 8px;
  color: #475569 !important;
  margin-bottom: 4px;
  min-height: 42px;
  padding: 0 12px !important;

  .v-list-item__icon {
    margin: 9px 12px 9px 0 !important;
  }

  .v-icon {
    color: #64748b !important;
    font-size: 21px;
  }

  .v-list-item__title {
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
  }
}

.drawer-list__item--active.v-list-item,
.drawer-list__item.v-list-item--active {
  background: #e9f7f5 !important;
  color: #0f766e !important;

  &::before {
    opacity: 0 !important;
  }

  .v-icon {
    color: #0f766e !important;
  }
}

.drawer-section {
  border-top: 1px solid rgba(31, 41, 55, 0.08);
  margin-top: 14px;
  padding-top: 14px;
}

.drawer-section__title {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 12px 8px;
  text-transform: uppercase;
}

.drawer-list__item--secondary.v-list-item {
  min-height: 38px;

  .v-icon {
    font-size: 19px;
  }

  .v-list-item__title {
    font-size: 13px;
    white-space: normal;
  }
}

.drawer-spacer {
  flex: 1;
}

.drawer-footer {
  border-top: 1px solid rgba(31, 41, 55, 0.08);
  display: grid;
  gap: 8px;
  padding-top: 14px;
}

.drawer-footer__button.v-btn {
  border-radius: 8px;
  justify-content: flex-start;
  letter-spacing: 0;
  min-height: 42px;
  text-transform: none;
}

.drawer-footer__button--primary.v-btn {
  color: #ffffff !important;
  justify-content: center;
}

.print {
  display: none;
  width: 100%;

  table {
    margin-top: 20px;
    width: 100%;
    page-break-after: always;

    .stock-column {
      text-align: center;
      width: 100px;
    }

    tr,
    td {
      border: 1px solid #000;
      padding: 5px;
    }
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .print {
    display: block;
  }
}
</style>
